<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSession } from '../lib/session'
import { IconFilm, IconGrid, IconLogout, IconScroll, IconUsers } from './Icons'
import { cx } from './ui'
import { Spinner } from './ui'

const NAV = [
  { to: '/', label: '概览', icon: IconGrid, end: true },
  { to: '/shows', label: '剧', icon: IconFilm, end: false },
  { to: '/users', label: '账号', icon: IconUsers, end: false },
  { to: '/audit', label: '日志', icon: IconScroll, end: false },
]

const { user, status, logout } = useSession()
const route = useRoute()
const router = useRouter()

watch(
  status,
  (s) => {
    if (s === 'anonymous') {
      void router.replace({ path: '/login', query: { from: route.fullPath } })
    }
  },
  { immediate: true },
)

function isActive(to: string, end: boolean) {
  if (end) return route.path === to
  return route.path === to || route.path.startsWith(`${to}/`)
}

const checking = computed(() => status.value === 'checking')
</script>

<template>
  <div v-if="checking" class="flex min-h-screen items-center justify-center gap-3 text-muted">
    <Spinner :size="18" />
    <span class="text-[13px]">校验会话…</span>
  </div>
  <div v-else-if="status === 'authenticated'" class="flex min-h-screen">
    <aside class="fixed inset-y-0 left-0 z-20 flex w-[220px] flex-col bg-[var(--color-rail)] text-white">
      <div class="px-4 py-5">
        <img src="/logo.png" alt="Reellax" class="h-7 w-auto" />
      </div>

      <nav class="mt-2 flex flex-col gap-1 px-3" aria-label="主导航">
        <RouterLink
          v-for="item in NAV"
          :key="item.to"
          :to="item.to"
          :class="
            cx(
              'group flex items-center gap-2.5 rounded-md px-3 py-2.5 text-[13.5px] transition-colors duration-150',
              isActive(item.to, item.end)
                ? 'bg-[var(--color-rail-hover)] font-semibold text-white'
                : 'text-[var(--color-rail-muted)] hover:bg-[var(--color-rail-hover)] hover:text-white',
            )
          "
        >
          <span
            :class="
              cx(
                'h-5 w-0.5 shrink-0 rounded-full transition-colors',
                isActive(item.to, item.end)
                  ? 'bg-[var(--color-accent)]'
                  : 'bg-transparent group-hover:bg-[var(--color-rail-line)]',
              )
            "
          />
          <component :is="item.icon" :size="15" />
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="mt-auto border-t border-[var(--color-rail-line)] px-4 py-4">
        <div class="flex items-center justify-between gap-2">
          <span
            class="min-w-0 truncate text-[12px] text-[var(--color-rail-muted)]"
            :title="user?.email"
          >
            {{ user?.email }}
          </span>
          <button
            type="button"
            title="登出"
            aria-label="登出"
            class="rounded-md p-1.5 text-[var(--color-rail-muted)] transition-colors hover:bg-[var(--color-rail-hover)] hover:text-white"
            @click="logout"
          >
            <IconLogout :size="15" />
          </button>
        </div>
      </div>
    </aside>

    <div class="ml-[220px] min-w-0 flex-1">
      <RouterView />
    </div>
  </div>
</template>
